/**
 * Home-screen card face: a gold Florin-branded rendering of the user's
 * virtual card, with an inline "show card number" reveal (PSE iframes).
 * The last four digits are cached in settings after the first fetch.
 */
import { useEffect, useRef, useState } from "react";
import * as gp from "../lib/gnosispay/api";
import { updateSettings, useAppState } from "../lib/store";
import { revealCardInto } from "./CardDetails";
import type { PseElementHandle } from "../lib/pse";

const face: React.CSSProperties = {
  borderRadius: 16,
  padding: "18px 20px",
  minHeight: 120,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  background: "linear-gradient(135deg, #7a5f16 0%, #d4af37 45%, #f1d97a 55%, #b8912a 100%)",
  color: "#20180a",
  boxShadow: "0 8px 22px rgba(0,0,0,0.35)",
  marginTop: 14,
};

export function VirtualCard() {
  const s = useAppState();
  const [last4, setLast4] = useState(s.settings.cardLast4 ?? "");
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const frameRef = useRef<PseElementHandle | null>(null);
  const requestRef = useRef(0);

  // First mount after onboarding won't have the digits cached yet.
  useEffect(() => {
    if (s.settings.demoMode || last4) return;
    void gp
      .withSession(() => gp.getCards())
      .then(async (cards) => {
        const digits = cards[0]?.lastFourDigits;
        if (digits) {
          setLast4(digits);
          await updateSettings({ cardLast4: digits });
        }
      })
      .catch(() => undefined); // face still renders with •••• placeholders
  }, [s.settings.demoMode, last4]);

  useEffect(() => () => {
    requestRef.current += 1;
    frameRef.current?.destroy();
  }, []);

  if (s.settings.demoMode) return null;

  const toggle = async () => {
    if (revealed) {
      requestRef.current += 1;
      frameRef.current?.destroy();
      frameRef.current = null;
      setBusy(false);
      setRevealed(false);
      return;
    }
    const request = ++requestRef.current;
    setBusy(true);
    setError("");
    try {
      setRevealed(true);
      await new Promise((r) => setTimeout(r, 30)); // let the container paint
      const frame = await revealCardInto("#pse-home-card", (message) => {
        if (requestRef.current === request) setError(message);
      });
      if (requestRef.current !== request) frame.destroy();
      else frameRef.current = frame;
    } catch (err) {
      if (requestRef.current !== request) return;
      setRevealed(false);
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      if (requestRef.current === request) setBusy(false);
    }
  };

  return (
    <>
      <div style={face}>
        <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800, letterSpacing: 1.5 }}>
          <span>FLORIN</span>
          <span style={{ fontStyle: "italic", fontWeight: 900 }}>VISA</span>
        </div>
        <div style={{ fontSize: 20, letterSpacing: 3, fontWeight: 700, margin: "14px 0 10px" }}>
          •••• •••• •••• {last4 || "••••"}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
          <span style={{ fontSize: 11, opacity: 0.75 }}>Virtual card · powered by Gnosis Pay</span>
          <button
            className="btn-ghost"
            style={{ fontSize: 12, padding: "4px 10px", color: "#20180a", border: "1px solid rgba(32,24,10,0.4)", borderRadius: 8, background: "rgba(255,255,255,0.25)" }}
            disabled={busy}
            onClick={() => void toggle()}
          >
            {busy ? "Loading…" : revealed ? "Hide" : "Show number"}
          </button>
        </div>
      </div>
      {revealed && (
        <div className="card" style={{ marginTop: 10 }}>
          {/* Explicit height — the PSE iframe is height:100% (0px against
              min-height-only parents). */}
          <div
            id="pse-home-card"
            style={{ height: 190, background: "#fff", borderRadius: 8, overflow: "hidden" }}
          />
        </div>
      )}
      {error && <div className="notice">{error}</div>}
    </>
  );
}
